import { getClient } from '@/api/api-client';
import { getTenantLedgerStatements } from '@/api/finance.api';
import {
    type IFetchRentTenanciesOverviewParams,
    fetchActiveRentTenanciesOfManyEstate,
    fetchMonthlyRentGuarantees,
    fetchRentTenanciesOfEstate,
    fetchRentalContractOverview,
    generateRentArrearNotices,
    getEntitySetting,
} from '@/api/property-management.api';
import {
    generalRentProblemCodes,
    generalTenancyProblemCodes,
    getProblemsMessagesFromInvestorTenancies,
    rentSpecificProblemCodes,
    tenancySpecificProblemCodes,
} from '@/components/processes/property-management/property-management.helpers';
import type { MaybeRef } from '@/shared/typings';
import { type IFindMonthlyRentGuaranteeParams, type IGenerateRentArrearNoticeParams, type IRentTenancy, RentTenancy } from '@condo/domain';
import type {
    FindMonthlyRentGuaranteesQueryParams,
    FindTenantsQueryParams,
    GetEstateRentTenanciesQueryParams,
    GetRentTenancyQueryParams,
} from '@condo/sdk/propx';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { computed, defineAsyncComponent, h, isRef, ref, unref } from 'vue';
import { useToast } from 'vue-toastification';
import { ToastType, showModal, withLoading } from '../shared';

export const useRentGuarantees = (paramsOrRef: MaybeRef<IFindMonthlyRentGuaranteeParams>) => {
    const params = computed(() => unref(paramsOrRef));

    return useQuery(['monthly-rent-guarantees', params], () => fetchMonthlyRentGuarantees(params.value as FindMonthlyRentGuaranteesQueryParams), {
        initialData: {
            monthlyRentGuarantees: [],
            total: 0,
        },
    });
};

export const useRentGuaranteesDateRange = () => {
    return useQuery(['monthly-rent-guarantees', 'date-range'], () => getClient('propx').getMonthlyRentGuaranteeRange(), {
        initialData: {
            min: null as Date | null,
            max: null as Date | null,
        },
    });
};

export const useRentTenancy = (rentTenancyId?: number, opts?: GetRentTenancyQueryParams) => {
    return useQuery(
        ['rent-tenancy', rentTenancyId, opts],
        async (): Promise<RentTenancy> =>
            withLoading(async () => {
                const data = await getClient('propx').getRentTenancy({ rentTenancyId: rentTenancyId! }, opts);
                return new RentTenancy(data as IRentTenancy);
            }),
        {
            initialData: null,
            enabled: !!rentTenancyId,
        },
    );
};

export const useTenantsByEstateId = (estateId: number, opts?: FindTenantsQueryParams) => {
    return useQuery(['tenants', 'estate', estateId], async () => {
        const data = await getClient('propx').findTenants({ estateId }, opts);
        return data;
    });
};

export const useRecalculateRentBalance = (rentTenancyId: MaybeRef<number>) => {
    const queryClient = useQueryClient();
    return useMutation(
        async () => {
            await getClient('propx').recalculateRentBalance({ rentTenancyId: unref(rentTenancyId) });
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['rent-tenancy'] });
            },
        },
    );
};

export const useGetLegacyRentBalance = (rentTenancyId: MaybeRef<number>) => {
    return useQuery(['rent-tenancy', rentTenancyId], async () => getClient('propx').recalculateLegacyRentBalance({ rentTenancyId: unref(rentTenancyId) }), {
        enabled: false,
    });
};

export const useEstateRentTenancies = (
    estateIdOrRef: MaybeRef<number | undefined>,
    opts?: GetEstateRentTenanciesQueryParams,
    enabledOrRef?: MaybeRef<boolean>,
) => {
    const enabled = computed(() => unref(enabledOrRef) && !!unref(estateIdOrRef));

    return useQuery(
        ['rent-tenancy', 'estate', unref(estateIdOrRef), opts],
        async () => (unref(estateIdOrRef) ? fetchRentTenanciesOfEstate(unref(estateIdOrRef)!, opts) : []),
        {
            initialData: [],
            enabled,
        },
    );
};

export const useEntitySetting = (estateIdOrRef: MaybeRef<number>) => {
    const estateId = computed(() => unref(estateIdOrRef));

    return useQuery(['entity-setting', 'estate', estateId], () => getEntitySetting(estateId.value));
};

export const useRentTenanciesOverview = (params?: MaybeRef<IFetchRentTenanciesOverviewParams>) => {
    const queryParams = computed(() => unref(params));

    return useQuery(['rent-tenancy', 'overview', queryParams], async () => fetchRentalContractOverview(queryParams.value), {
        initialData: { rentTenancies: [] as RentTenancy[], count: 0 },
    });
};

export const useClearRentTenancyCache = () => {
    const queryClient = useQueryClient();
    return () => queryClient.invalidateQueries({ queryKey: ['rent-tenancy'] });
};

export const useRegisteredTenants = () => {
    return useQuery(
        ['registered-tenants'],
        async () => {
            return getClient('propx').getRegisteredTenants();
        },
        {
            initialData: [],
        },
    );
};

export const useTenants = () => {
    return useQuery(
        ['tenants'],
        async () => {
            const data = await getClient('propx').getTenants();
            return data;
        },
        {
            initialData: [],
        },
    );
};

export const useRentArrearsNotice = (onFinish?: () => any) => {
    // @ts-expect-error
    const RentArrearNoticeModal = defineAsyncComponent(() => import('@/components/processes/property-management/RentArrearNoticeModal.vue'));

    const toast = useToast();
    const loading = ref(false);

    const showArrearNoticeModal = (rentTenancies: (IRentTenancy | RentTenancy)[]) => {
        const modal = showModal(RentArrearNoticeModal, {
            // height: 600,
            width: 600,
            props: { rentTenancies },
            maskClosable: false,
            events: {
                generate: async (payload: IGenerateRentArrearNoticeParams[]) => {
                    try {
                        modal.close();
                        loading.value = true;
                        const driveFolderId = await generateRentArrearNotices(payload);
                        toast(
                            () =>
                                h('div', [
                                    h('span', 'Documents are being generated'),
                                    h('br'),
                                    h(
                                        'a',
                                        { href: `https://drive.google.com/drive/folders/${driveFolderId}?q=type:pdf`, target: '_blank' },
                                        'Open Google Drive Folder',
                                    ),
                                ]),
                            {
                                icon: false,
                                type: ToastType.DEFAULT,
                                timeout: 10_000,
                                toastClassName: 'text-toast',
                            },
                        );
                        onFinish?.();
                    } finally {
                        loading.value = false;
                    }
                    modal.close();
                },
                close: () => {
                    modal.close();
                },
            },
        });
    };

    return { loading, generate: showArrearNoticeModal };
};

export const useWarnings = (rentTenanciesOrRef: MaybeRef<RentTenancy[]>, estateIdOrRef: MaybeRef<number>) => {
    const rentTenancies = computed(() => unref(rentTenanciesOrRef));
    const estateId = computed(() => unref(estateIdOrRef));

    const warnings = computed(() => getProblemsMessagesFromInvestorTenancies(rentTenancies.value, estateId.value));

    const generalTenancyWarnings = warnings.value.filter(warning => generalTenancyProblemCodes.includes(warning.problem.code));

    const getTenancySpecificWarnings = (tenancyId: number) =>
        warnings.value.filter(warning => tenancySpecificProblemCodes.includes(warning.problem.code) && warning.problem.rentTenancyId === tenancyId);

    const getGeneralRentWarnings = (tenancyId: number) =>
        warnings.value.filter(warning => generalRentProblemCodes.includes(warning.problem.code) && warning.problem.rentTenancyId === tenancyId);

    const getRentSpecificWarnings = (tenancyId: number) =>
        warnings.value.filter(warning => rentSpecificProblemCodes.includes(warning.problem.code) && warning.problem.rentTenancyId === tenancyId);

    return { warnings, generalTenancyWarnings, getTenancySpecificWarnings, getGeneralRentWarnings, getRentSpecificWarnings };
};

export const useTenantLedgerStatements = (rentTenancyId: MaybeRef<number>) => {
    const rentTenancyIdParam = isRef(rentTenancyId) ? unref(rentTenancyId) : rentTenancyId;
    return useQuery(['rent-tenancy-ledger-statement', rentTenancyId], async () => getTenantLedgerStatements(rentTenancyIdParam), {
        initialData: [],
    });
};

export const useActiveRentTenanciesOfManyEstate = (estateIds: MaybeRef<number[]>) => {
    return useQuery(['active-rent-tenancies-of-many-estate', estateIds], async () => fetchActiveRentTenanciesOfManyEstate(unref(estateIds)), {
        initialData: [],
        enabled: computed(() => unref(estateIds).length > 0),
    });
};
